import { Button, Flex, Group, Modal, Pagination } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus, IconRefresh } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { FixedPageTitle } from '../layout/PageWithFixedToolbar/FixedPageTitle'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { DeviceList, TableSortConfig } from './DeviceList/DeviceList'
import { DeviceRegistrationSteps } from './DeviceRegistrationSteps/DeviceRegistrationSteps'

const PAGE_SIZE = 20

const DEFAULT_SORT_CONFIG: TableSortConfig = {
  key: 'label',
  order: 'asc'
}

export const DeviceListPage = () => {
  const [canCreateDevice] = useHasPermissions(Resource.Devices, [
    CrudAction.Create
  ])

  const [
    isAddDeviceModalOpened,
    { open: openAddDeviceModal, close: closeAddDeviceModal }
  ] = useDisclosure(false)

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    sortConfig: DEFAULT_SORT_CONFIG
  })

  const { activePage, sortConfig } = tableState

  const { data, isFetching, isLoading, isError, refetch } = useGetDeviceList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: buildSortParams(sortConfig).ordering
    },
    {
      keepPreviousData: true
    }
  )

  const devices = data?.results || []

  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleDeviceRefresh = () => {
    void refetch()
    closeAddDeviceModal()
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <FixedPageTitle
          title={<FormattedMessage id="devices.list.title" />}
          leftSection={
            <Group gap="xs" wrap="nowrap">
              {canCreateDevice && (
                <Button
                  size="xs"
                  miw={140}
                  leftSection={<IconPlus size={16} />}
                  onClick={openAddDeviceModal}
                >
                  <FormattedMessage id="devices.list.buttons.create" />
                </Button>
              )}

              <Button
                size="xs"
                miw={120}
                variant="outline"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void refetch()}
              >
                <FormattedMessage id="refresh" />
              </Button>
            </Group>
          }
        />
      }
    >
      <DeviceList
        devices={devices}
        sortConfig={sortConfig}
        isLoading={isLoading || isFetching}
        hasError={isError}
        onSort={(sortConfig) => setTableState({ activePage: 1, sortConfig })}
        onReload={() => void refetch()}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <Modal
        size="xl"
        opened={isAddDeviceModalOpened}
        title={<FormattedMessage id="devices.list.buttons.create" />}
        onClose={closeAddDeviceModal}
      >
        <DeviceRegistrationSteps onRefresh={handleDeviceRefresh} />
      </Modal>
    </PageWithFixedToolbar>
  )
}
